import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, idempotencyKey, unwrap } from '@/api/client'
import type { Schemas } from '@/api/types'
import { orderKeys } from '@/features/orders/queries'

export const qcKeys = {
  list: (orderId: string) => ['qc-remarks', orderId] as const,
}

export function useQCRemarks(orderId: string) {
  return useQuery({
    queryKey: qcKeys.list(orderId),
    queryFn: () =>
      unwrap(api.GET('/api/v1/orders/{order_id}/qc-remarks', { params: { path: { order_id: orderId } } })),
  })
}

export function useCreateQCRemark(orderId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Schemas['QCRemarkCreateRequest']) =>
      unwrap(
        api.POST('/api/v1/orders/{order_id}/qc-remarks', {
          body,
          params: { path: { order_id: orderId }, header: { 'Idempotency-Key': idempotencyKey() } },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qcKeys.list(orderId) })
      void qc.invalidateQueries({ queryKey: orderKeys.history(orderId) })
    },
  })
}

export type QCSimpleTransition = 'acknowledge' | 'resolve' | 'verify'
export type QCReasonTransition = 'reject' | 'reopen'

const SIMPLE_PATHS = {
  acknowledge: '/api/v1/qc-remarks/{id}/acknowledge',
  resolve: '/api/v1/qc-remarks/{id}/resolve',
  verify: '/api/v1/qc-remarks/{id}/verify',
} as const

const REASON_PATHS = {
  reject: '/api/v1/qc-remarks/{id}/reject',
  reopen: '/api/v1/qc-remarks/{id}/reopen',
} as const

export function useQCSimpleTransition(orderId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; transition: QCSimpleTransition; version: number; comment?: string }) =>
      unwrap(
        api.POST(SIMPLE_PATHS[vars.transition], {
          params: { path: { id: vars.id }, header: { 'Idempotency-Key': idempotencyKey() } },
          body: { version: vars.version, comment: vars.comment },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qcKeys.list(orderId) }),
  })
}

export function useQCReasonTransition(orderId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; transition: QCReasonTransition; version: number; reason: string }) =>
      unwrap(
        api.POST(REASON_PATHS[vars.transition], {
          params: { path: { id: vars.id }, header: { 'Idempotency-Key': idempotencyKey() } },
          body: { version: vars.version, reason: vars.reason },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qcKeys.list(orderId) }),
  })
}
